import { createContext, FC, useEffect, useMemo, useContext, ReactNode } from "react";
import { useDisclosure, usePrevious } from "@chakra-ui/react";
import { useUser } from "../hooks/useUser";
import { usePastimeToast } from "../components/Toast";
import { messages } from "../locale";
import { Analytics } from "../../analytics";
import { dynamicConfig } from "../../config";

interface InternetConnectionContextType {
  isOpenConnection: boolean;
}

const InternetConnectionContext = createContext<InternetConnectionContextType>({
  isOpenConnection: navigator.onLine,
});

export const useInternetConnection = () => useContext(InternetConnectionContext);

export const InternetConnectionContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { checkInternetConnection } = dynamicConfig;

  const {
    isOpen: isOpenConnection,
    onOpen: onOpenConnection,
    onClose: onCloseConnection,
  } = useDisclosure({ defaultIsOpen: !checkInternetConnection ? true : navigator.onLine });
  const previousConnectionState = usePrevious(isOpenConnection);

  const { loadApp } = useUser();

  const toast = usePastimeToast();
  const { connectionRestored, lostConnection } = messages.internetConnection;

  useEffect(() => {
    if (isOpenConnection && previousConnectionState === false) {
      loadApp();
      toast({ title: connectionRestored });
      Analytics.track("Internet Connection Restored");
    }

    if (!isOpenConnection && previousConnectionState) {
      toast({
        status: "error",
        title: lostConnection,
      });
    }
  }, [isOpenConnection, previousConnectionState, toast, connectionRestored, lostConnection, loadApp]);

  useEffect(() => {
    if (checkInternetConnection) {
      window.addEventListener("online", onOpenConnection, false);
      window.addEventListener("offline", onCloseConnection, false);
    }

    return () => {
      window.removeEventListener("online", onOpenConnection);
      window.removeEventListener("offline", onCloseConnection);
    };
  }, []); // eslint-disable-line

  const value = useMemo(() => ({ isOpenConnection }), [isOpenConnection]);

  return <InternetConnectionContext.Provider value={value}>{children}</InternetConnectionContext.Provider>;
};
